/**
 * Geometry for the complexity progress bar where it merges into the chevron
 * array (MUS-401).
 *
 * The bar and the chevrons used to be separate shapes with separate borders.
 * Design wants them to read as *one* polygon with a single continuous border
 * running around the whole thing.
 *
 * The two breakpoints get there differently. Mobile is new artwork, so it is a
 * single SVG path stroked once. Desktop has to preserve the existing chevron
 * array exactly — gradient wash, hairline outlines, no gaps — which that array
 * achieves by layering an outer green `clip-path` under an inset dark one,
 * since `clip-path` cannot stroke. So desktop emits `clip-path` polygons for
 * the same technique, and only the first chevron's polygon changes.
 *
 * Everything is pure: these functions take a layout and return path or
 * `clip-path` strings, so the shapes are unit-testable without a DOM.
 */

/** A point in the SVG user space of the graphic. */
interface Point {
  x: number;
  y: number;
}

const round = (value: number): number => Math.round(value * 1000) / 1000;

const moveTo = ({ x, y }: Point): string => `M${round(x)},${round(y)}`;
const lineTo = ({ x, y }: Point): string => `L${round(x)},${round(y)}`;

/**
 * An elliptical arc to `to`. `sweep` 1 curves clockwise in SVG's y-down space
 * (an outer/convex corner); 0 curves anticlockwise (the concave fillet where a
 * thin section meets a taller one).
 */
const arcTo = (radius: number, to: Point, sweep: 0 | 1): string =>
  `A${round(radius)},${round(radius)} 0 0 ${sweep} ${round(to.x)},${round(to.y)}`;

/* ------------------------------------------------------------------ mobile */

export interface MobileBarLayout {
  /** Total width of the graphic, in SVG user units. */
  width: number;
  /** Height of the thin progress strip. */
  barHeight: number;
  /** Height of the taller pill holding the chevrons and the accuracy readout. */
  pillHeight: number;
  /** Width of that pill. */
  pillWidth: number;
  /**
   * Gap between the pill's right edge and the graphic's, so the pill does not
   * sit flush against the screen.
   */
  rightPadding: number;
  /** Matching gap before the strip's left cap. */
  leftPadding: number;
  /**
   * Radius of the concave fillet easing the strip's bottom edge down into the
   * pill's left edge. This is the join that makes the two read as one shape.
   */
  filletRadius: number;
}

/**
 * Below this much exposed track the merged path degenerates (the fillet and the
 * strip's cap have no room), so the figure is emitted as the bare pill instead.
 */
const MIN_TRACK_EXTENT = 1;

/** Left and right edges of the pill, inset from the graphic's right edge. */
export function mobilePillBounds(layout: MobileBarLayout): { left: number; right: number } {
  const right = layout.width - layout.rightPadding;
  return { left: right - layout.pillWidth, right };
}

/** The chevron pill on its own: a plain rounded rect, all four corners. */
export function mobilePillPath(layout: MobileBarLayout): string {
  const { pillHeight } = layout;
  const radius = pillHeight / 2;
  const { left, right } = mobilePillBounds(layout);

  return [
    moveTo({ x: left + radius, y: 0 }),
    lineTo({ x: right - radius, y: 0 }),
    arcTo(radius, { x: right, y: radius }, 1),
    lineTo({ x: right, y: pillHeight - radius }),
    arcTo(radius, { x: right - radius, y: pillHeight }, 1),
    lineTo({ x: left + radius, y: pillHeight }),
    arcTo(radius, { x: left, y: pillHeight - radius }, 1),
    lineTo({ x: left, y: radius }),
    arcTo(radius, { x: left + radius, y: 0 }, 1),
    'Z',
  ].join(' ');
}

/**
 * The mobile figure: the rounded chevron pill, with the thin progress strip
 * extending left out of it as far as `trackLeft`.
 *
 * Pill and strip share a top edge, so the silhouette only steps on the
 * underside — one concave fillet is enough to hide the seam. Passing
 * `trackLeft` at the pill's own left edge collapses the figure back to the bare
 * pill, which is what makes the paused/playing transition a single continuous
 * morph: the bar literally grows out of the chevrons.
 *
 * Drawn clockwise from the strip's left cap.
 */
export function mobileMergedOutlinePath(layout: MobileBarLayout, trackLeft: number): string {
  const { barHeight, pillHeight, filletRadius } = layout;
  const barRadius = barHeight / 2;
  const pillRadius = pillHeight / 2;
  const { left: pillLeft, right: pillRight } = mobilePillBounds(layout);

  const trackExtent = pillLeft - Math.min(trackLeft, pillLeft);
  if (trackExtent < MIN_TRACK_EXTENT) return mobilePillPath(layout);

  // The fillet can eat neither past the pill's own bottom-left corner, nor back
  // further than the track is long.
  const fillet = Math.max(
    0,
    Math.min(filletRadius, pillHeight - barHeight - pillRadius, trackExtent - barRadius),
  );
  const capLeft = pillLeft - trackExtent;

  return [
    moveTo({ x: capLeft + barRadius, y: 0 }),
    lineTo({ x: pillRight - pillRadius, y: 0 }),
    arcTo(pillRadius, { x: pillRight, y: pillRadius }, 1),
    lineTo({ x: pillRight, y: pillHeight - pillRadius }),
    arcTo(pillRadius, { x: pillRight - pillRadius, y: pillHeight }, 1),
    lineTo({ x: pillLeft + pillRadius, y: pillHeight }),
    arcTo(pillRadius, { x: pillLeft, y: pillHeight - pillRadius }, 1),
    lineTo({ x: pillLeft, y: barHeight + fillet }),
    arcTo(fillet, { x: pillLeft - fillet, y: barHeight }, 0),
    lineTo({ x: capLeft + barRadius, y: barHeight }),
    arcTo(barRadius, { x: capLeft + barRadius, y: 0 }, 1),
    'Z',
  ].join(' ');
}

/**
 * How far right the mobile progress fill may reach.
 *
 * Not the pill's left edge. The merged figure runs a straight top edge across
 * the join, but the chevron array is clipped to the *pill*, whose top-left
 * corner is rounded — so between the pill's edge and its cap radius there is a
 * wedge that the chevrons do not paint and a fill stopping at the edge does not
 * reach either. It shows as a dark notch at the join.
 *
 * Clearing the cap radius closes it. This needs more compensation than the
 * desktop equivalent, whose chevron meets the strip on a straight diagonal
 * rather than around a curve.
 *
 * Overshooting is safe: the chevrons are drawn over the fill, so any green past
 * the pill's edge is hidden behind them.
 */
export function mobileFillLimit(layout: MobileBarLayout): number {
  return mobilePillBounds(layout).left + layout.pillHeight / 2;
}

/**
 * The paused figure: the strip alone, with no track and no pill.
 *
 * On pause the chevron pill leaves the top of the screen and the unfilled
 * track retracts into it, so all that remains is the green itself — a narrow
 * capsule as wide as the current fill.
 */
export function mobilePausedFillPath(left: number, right: number, barHeight: number): string {
  const span = Math.max(right - left, 0);
  const radius = Math.min(barHeight / 2, span / 2);
  return [
    moveTo({ x: left + radius, y: 0 }),
    lineTo({ x: right - radius, y: 0 }),
    arcTo(radius, { x: right - radius, y: barHeight }, 1),
    lineTo({ x: left + radius, y: barHeight }),
    arcTo(radius, { x: left + radius, y: 0 }, 1),
    'Z',
  ].join(' ');
}

/**
 * Right edge of the filled portion of the mobile strip, in absolute terms.
 *
 * The strip starts at its left padding rather than at x=0, so the fill is a
 * span between two edges rather than a width from the origin.
 */
export function mobileFillRight(layout: MobileBarLayout, fraction: number): number {
  const clamped = Math.min(Math.max(fraction, 0), 1);
  const left = layout.leftPadding;
  return round(left + (mobileFillLimit(layout) - left) * clamped);
}

/**
 * Slope of a chevron's flank, as point depth over half the shape's height.
 * Taken from the desktop array (63px over 80px) so both breakpoints lean at the
 * same angle regardless of how differently proportioned their boxes are.
 */
export const CHEVRON_SLOPE = 63 / 80;

export interface MobileChevronLayout {
  /** Left edge of the chevron array, in the graphic's coordinate space. */
  left: number;
  /** Width of the array. */
  width: number;
  /** Height of the pill the array fills. */
  height: number;
  /** How many chevrons tile that width. */
  count: number;
}

/**
 * How far the chevrons are drawn beyond the pill on every side.
 *
 * The array is clipped to the pill, so anything past its edge is trimmed. That
 * overshoot is what makes the chevrons cover the pill completely: without it
 * their outlines run along the pill's own top, bottom and end edges and read as
 * dark gaps, and the end chevrons stop at the pill's straight edge instead of
 * filling its rounded caps. Half the pill's height clears the cap radius.
 */
const mobileOvershoot = (height: number): number => height / 2;

/**
 * One chevron of the mobile array, as a fillable polygon.
 *
 * Unlike the desktop glyphs these tile their region completely, so each
 * chevron's convex point is exactly the concave notch of the one after it. The
 * two ends are special: the first chevron's left edge is a straight vertical
 * rather than a notch (it has no predecessor to receive), and the last
 * chevron's right edge is a straight vertical rather than a point. Both of
 * those edges sit outside the pill, which clips the array and rounds it.
 */
export function mobileChevronPath(layout: MobileChevronLayout, index: number): string {
  const { left, width, height, count } = layout;
  const pitch = width / count;
  const midY = height / 2;
  const depth = CHEVRON_SLOPE * midY;
  const overshoot = mobileOvershoot(height);

  const top = -overshoot;
  const bottom = height + overshoot;
  // Carrying the flank past the pill has to follow the flank's own direction,
  // or the visible slope changes with the overshoot.
  const flankShift = (depth * overshoot) / midY;

  // The array's visible marks are the boundaries between chevrons, and each of
  // those runs from its cell edge out to an apex `depth` further right. Their
  // midpoint therefore sits `depth / 2` right of the pill's own centre, which
  // reads as the whole array being off to the right. Shifting by that much
  // centres the marks; the ends stay covered because both overshoot the pill.
  const centringShift = depth / 2;
  const cellLeft = left + index * pitch - centringShift;
  const cellRight = left + (index + 1) * pitch - centringShift;
  const isFirst = index === 0;
  const isLast = index === count - 1;

  const points: Point[] = [{ x: isFirst ? left - overshoot : cellLeft - flankShift, y: top }];

  if (isLast) {
    // Straight right edge, out past the pill's right cap. Deliberately not
    // shifted: it only has to clear the cap, and leaving it put keeps the array
    // covering the pill's full width.
    points.push(
      { x: left + width + overshoot, y: top },
      { x: left + width + overshoot, y: bottom },
    );
  } else {
    points.push(
      { x: cellRight - flankShift, y: top },
      { x: cellRight + depth, y: midY },
      { x: cellRight - flankShift, y: bottom },
    );
  }

  points.push({ x: isFirst ? left - overshoot : cellLeft - flankShift, y: bottom });

  // The first chevron closes straight up its left edge; the rest fold inward to
  // receive the previous chevron's point.
  if (!isFirst) points.push({ x: cellLeft + depth, y: midY });

  return [moveTo(points[0]), ...points.slice(1).map(lineTo), 'Z'].join(' ');
}

/* ----------------------------------------------------------------- desktop */

/**
 * Desktop keeps the original chevron array verbatim — CSS `clip-path` polygons
 * layered outer-green over inset-dark to fake a hairline outline, with a
 * gradient wash on top. That look is load-bearing, so rather than redrawing it
 * as SVG these helpers emit `clip-path` values for the same technique. Only the
 * first chevron changes: its polygon grows leftward into the progress strip so
 * the two share one silhouette.
 *
 * Coordinates are absolute pixels within a container spanning the whole header
 * bar, because the merged polygon has to reach the header's left edge while the
 * chevrons stay pinned to its right.
 */
export interface DesktopBarLayout {
  /** Width of the header bar. */
  width: number;
  /** Height of the header bar; the chevrons span it. */
  height: number;
  /** Width reserved at the right for the chevron array. */
  chevronRegionWidth: number;
  /** Width of one chevron's box. */
  cellWidth: number;
  /** Length of a chevron's flat top and bottom edges, before the point. */
  bodyWidth: number;
  /**
   * Horizontal depth of the point and of the matching left notch. The two are
   * equal, which is what lets consecutive chevrons nest with no gap.
   */
  depth: number;
  /** Distance between consecutive chevrons. */
  pitch: number;
  /** Height of the progress strip along the header's bottom edge. */
  barHeight: number;
  /** Width of the faked outline — the inset between the outer and inner shapes. */
  hairline: number;
  /** Gap between the reserved region's left edge and the array's own box. */
  arrayInset: number;
  /** How much wider the backdrop is than the array box — the original's 110%. */
  backdropScale: number;
}

/** Matches the values the original stylesheet resolves to at a 160px header. */
export const DEFAULT_DESKTOP_LAYOUT: Omit<DesktopBarLayout, 'width' | 'height'> = {
  chevronRegionWidth: 300,
  cellWidth: 140,
  bodyWidth: 71.4,
  depth: 63,
  pitch: 72.8,
  // Not from the stylesheet like the chevron values above — the strip is new,
  // and this is the height design settled on.
  barHeight: 18,
  hairline: 1.4,
  arrayInset: 20,
  backdropScale: 1.1,
};

/** Notch depth of the black backing, as a fraction of its width. */
export const BACKING_NOTCH_FRACTION = 0.225;
/** Notch depth of the gradient wash, as a fraction of its width. */
export const WASH_NOTCH_FRACTION = 0.21;

/** Left edge of the chevron array within the header-wide container. */
export function desktopChevronLeft(layout: DesktopBarLayout): number {
  return layout.width - layout.chevronRegionWidth;
}

const polygon = (points: Point[]): string =>
  `polygon(${points.map(({ x, y }) => `${round(x)}px ${round(y)}px`).join(', ')})`;

/**
 * The vertices of one chevron, inset uniformly by `inset`.
 *
 * Insetting is not a uniform scale: the notch apex moves *right* as the shape
 * shrinks while the point moves *left*, because they face opposite directions.
 * The original stylesheet encodes exactly this — outer notch at 45% against an
 * inner at 46%, outer point at 96% against an inner at 95%.
 */
function chevronVertices(layout: DesktopBarLayout, index: number, inset: number) {
  const { height, bodyWidth, depth, pitch } = layout;
  const origin = desktopChevronLeft(layout) + index * pitch;
  return {
    leftEdge: origin + inset,
    apex: origin + depth + inset,
    bodyRight: origin + bodyWidth - inset,
    point: origin + bodyWidth + depth - inset,
    top: inset,
    bottom: height - inset,
    midY: height / 2,
  };
}

/**
 * Where the strip's top edge meets the first chevron's lower-left flank.
 *
 * The flank runs from the notch apex at mid-height down to the chevron's
 * bottom-left corner, so the strip joins it partway along rather than at the
 * chevron's own left edge — otherwise the silhouette shows a step.
 */
function flankMeetX(layout: DesktopBarLayout, inset: number): number {
  const { height, barHeight } = layout;
  const { leftEdge, apex, bottom, midY } = chevronVertices(layout, 0, inset);
  const barTop = height - barHeight + inset;
  const travel = bottom - midY;
  if (travel <= 0) return apex;
  const t = Math.min(Math.max((barTop - midY) / travel, 0), 1);
  return apex + (leftEdge - apex) * t;
}

/**
 * The merged silhouette: the progress strip plus the first chevron, as one
 * polygon. `inset` shrinks it to produce the inner (dark) copy whose offset
 * from the outer (green) copy reads as the hairline outline.
 */
export function desktopMergedClipPath(layout: DesktopBarLayout, inset = 0): string {
  const { height, barHeight } = layout;
  const { apex, bodyRight, point, top, bottom, midY } = chevronVertices(layout, 0, inset);
  const barTop = height - barHeight + inset;

  return polygon([
    { x: inset, y: barTop },
    { x: flankMeetX(layout, inset), y: barTop },
    { x: apex, y: midY },
    { x: chevronVertices(layout, 0, inset).leftEdge, y: top },
    { x: bodyRight, y: top },
    { x: point, y: midY },
    { x: bodyRight, y: bottom },
    { x: inset, y: bottom },
  ]);
}

/**
 * The strip's interior alone, for the progress fill to be clipped to. It stops
 * at the chevron's flank, so the green feeds the first chevron rather than
 * running underneath the array.
 */
export function desktopBarInteriorClipPath(layout: DesktopBarLayout): string {
  const { height, barHeight, hairline } = layout;
  const { leftEdge, bottom } = chevronVertices(layout, 0, hairline);
  const barTop = height - barHeight + hairline;

  return polygon([
    { x: hairline, y: barTop },
    { x: flankMeetX(layout, hairline), y: barTop },
    { x: leftEdge, y: bottom },
    { x: hairline, y: bottom },
  ]);
}

/** Width of the backdrop element behind the array — the original's 110%. */
export function desktopBackdropWidth(layout: DesktopBarLayout): number {
  return (layout.chevronRegionWidth - layout.arrayInset) * layout.backdropScale;
}

/**
 * The backdrop behind the chevron array — the black backing beneath it and the
 * gradient wash above it, which differ only in how deep their notch cuts.
 *
 * Coordinates are **local to the backdrop element**, which is positioned at the
 * array's left edge and sized by {@link desktopBackdropWidth}, rather than to
 * the header-wide container the chevrons use. That matters for the wash: a
 * `linear-gradient` ramps across its own element, so stretching the element the
 * full width of the header would spend most of the ramp on the progress strip
 * and leave the array washed with the transparent tail. The original sized this
 * element to the array for exactly that reason.
 *
 * The left edge carries the same notch the first chevron does; the right edge
 * is straight and falls off the side of the screen.
 */
export function desktopBackdropClipPath(layout: DesktopBarLayout, notchFraction: number): string {
  const { height } = layout;
  const width = desktopBackdropWidth(layout);

  return polygon([
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
    { x: width * notchFraction, y: height / 2 },
  ]);
}

/**
 * Right edge of a squared (half) chevron.
 *
 * It has to clear the header's own width, not merely the chevron cell: the
 * border runs along the top and bottom of this shape, and stopping it at the
 * cell would leave the last stretch before the screen edge unbordered.
 */
function squaredRightEdge(layout: DesktopBarLayout, index: number): number {
  const { width, pitch, cellWidth, chevronRegionWidth, arrayInset, backdropScale } = layout;
  const cellRight = desktopChevronLeft(layout) + index * pitch + cellWidth;
  const backdropRight =
    desktopChevronLeft(layout) + (chevronRegionWidth - arrayInset) * backdropScale;
  return Math.max(cellRight, backdropRight, width) + 1;
}

/**
 * One chevron's outer (green) silhouette. Index 0 is the chevron merged with
 * the strip, so it is only meaningful for indices 1 and up.
 *
 * `squared` drops the point and closes the shape with a straight right edge.
 * The array's last chevron is drawn that way: it keeps the concave notch that
 * receives the previous chevron's point, but has no point of its own, because
 * its right edge falls off the side of the screen. Rendering it as a whole
 * chevron would put a point on screen that was never meant to be seen.
 */
export function desktopChevronClipPath(
  layout: DesktopBarLayout,
  index: number,
  squared = false,
): string {
  const { leftEdge, apex, bodyRight, point, top, bottom, midY } = chevronVertices(layout, index, 0);
  const right = squaredRightEdge(layout, index);

  if (squared) {
    return polygon([
      { x: leftEdge, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
      { x: leftEdge, y: bottom },
      { x: apex, y: midY },
    ]);
  }

  return polygon([
    { x: leftEdge, y: top },
    { x: bodyRight, y: top },
    { x: point, y: midY },
    { x: bodyRight, y: bottom },
    { x: leftEdge, y: bottom },
    { x: apex, y: midY },
  ]);
}

/**
 * One chevron's interior, for the dark unfilled fill — or, when the chevron is
 * earned, for the green that replaces it. `squared` matches
 * {@link desktopChevronClipPath}.
 */
export function desktopChevronInteriorClipPath(
  layout: DesktopBarLayout,
  index: number,
  squared = false,
): string {
  const { hairline } = layout;
  const { leftEdge, apex, bodyRight, point, top, bottom, midY } = chevronVertices(
    layout,
    index,
    hairline,
  );
  // No inset on the squared edge: it is off screen, so a hairline there would
  // only ever show if the header got narrower than the array.
  const right = squaredRightEdge(layout, index);

  if (squared) {
    return polygon([
      { x: leftEdge, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
      { x: leftEdge, y: bottom },
      { x: apex, y: midY },
    ]);
  }

  return polygon([
    { x: leftEdge, y: top },
    { x: bodyRight, y: top },
    { x: point, y: midY },
    { x: bodyRight, y: bottom },
    { x: leftEdge, y: bottom },
    { x: apex, y: midY },
  ]);
}

/**
 * How far right the desktop progress fill may reach.
 *
 * Not the chevron's left edge: the chevron's left boundary is the diagonal
 * flank, so a fill stopping at the leftmost point of that diagonal leaves an
 * unfilled wedge above it. Running the fill to the notch apex lets
 * {@link desktopBarInteriorClipPath} shape the edge against the flank instead,
 * and the green arrives filled all the way in.
 */
export function desktopFillLimit(layout: DesktopBarLayout): number {
  return desktopChevronLeft(layout) + layout.depth;
}

/**
 * Width of the filled portion, in SVG user units.
 *
 * `limit` is where the green tops out — the left edge of the chevron region,
 * *not* the width of the figure. The bar feeds the chevrons rather than
 * competing with them: it runs 0 -> `limit` as the user climbs toward the
 * chevron activation threshold (CAT), and once they reach it the bar sits full
 * against the first chevron and the chevrons themselves carry progress from
 * there. A full bar therefore means "chevrons are live", not "tier complete".
 *
 * Kept separate from the outline so the fill can be clipped to the merged
 * silhouette, picking up the strip's rounded left cap without needing to know
 * the shape.
 */
export function fillWidth(limit: number, fraction: number): number {
  const clamped = Math.min(Math.max(fraction, 0), 1);
  return round(limit * clamped);
}
